import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import {
  AddBrandsThunk,
  GetSingleBrandsThunk,
  UpdateBrandsThunk,
} from "../../redux/states/brands/thunk";

const CreateUpdateBrands = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleBrand, loading, error } = useSelector((state) => state.brands);

  const [formData, setFormData] = useState({
    name: "",
    status: "active",
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // ✅ Load banner when updating
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleBrandsThunk(uuid));
    }
  }, [uuid, dispatch]);

  // ✅ Prefill form when banner data is fetched
  useEffect(() => {
    if (singleBrand && isUpdating) {
      setFormData({
        name: singleBrand.name || "",
        status: singleBrand.status || "active",
        type: singleBrand.type || "hero_section",
        image: null,
      });
    }
  }, [singleBrand, isUpdating]);

  // ✅ Handle input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // ✅ Submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) data.append(key, value);
    });
    if (isUpdating) data.append("uuid", uuid);

    if (isUpdating) {
      dispatch(UpdateBrandsThunk({ uuid, formData: data })).then((res) => {
        navigate("/brands");
      });
    } else {
      dispatch(AddBrandsThunk(data)).then((res) => {
        navigate("/brands");
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  {isUpdating ? "Update Brands" : "Add New Brands"}
                </h1>
                <p className="text-gray-600 mt-2">
                  {isUpdating
                    ? "Edit the Brands details below."
                    : "Fill the form to create a new Brands."}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-red-700">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Details */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Brands Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[["name", "Name *"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="block font-medium text-gray-700 mb-2">
                      {label}
                    </label>
                    <input
                      type="text"
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}

                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Banner" : "Save Banner"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateBrands;
